import { apiSlice } from './apiSlice'

export const posApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // --- POS Data ---
    getPosData: builder.query<any, void>({
      query: () => ({ url: '/pos/data_create_pos' }),
      providesTags: ['POS'],
    }),
    getTodaySales: builder.query<any, void>({
      query: () => ({ url: '/pos/today_sales' }),
      providesTags: ['POS'],
    }),
    getInvoice: builder.query<any, number | string>({
      query: (id) => ({ url: `/sales_print_invoice/${id}` }),
      providesTags: (result, error, id) => [{ type: 'POS', id }],
    }),
    createPosSale: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/pos/create_pos',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['POS', 'Sales', 'Products', 'PosProduct', 'Customer', 'PosCustomer', 'DashboardStats'],
    }),
    // Offline replay — batched and keyed by the client's stable sale id, so
    // the server can dedupe (user, client id) and a replayed sync cannot
    // double-charge stock or the AR ledger.
    syncPosSales: builder.mutation<any, { salesMap: Record<string, Record<string, any>> }>({
      query: (data) => ({
        url: '/sync',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['POS', 'Sales', 'Products', 'PosProduct', 'Customer', 'PosCustomer', 'DashboardStats'],
    }),
    getProductStock: builder.query<any, { productId: number | string; params?: Record<string, any> }>({
      query: ({ productId, params }) => ({
        url: `/pos/get_product_stock/${productId}`,
        params: params || {},
      }),
      providesTags: ['PosProduct'],
    }),
    getProductsByWarehouse: builder.query<any[], { warehouseId: number | string; params?: Record<string, any> }>({
      query: ({ warehouseId, params }) => ({
        url: `/get_products_by_warehouse/${warehouseId}`,
        params: params || {},
      }),
      providesTags: ['PosProduct'],
    }),

    // --- POS Drafts ---
    getPosDrafts: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/pos/draft_sales',
        params: params || {},
      }),
      providesTags: ['PosDraft'],
    }),
    getPosDraftById: builder.query<any, number | string>({
      query: (id) => ({ url: `/pos/draft_sales/${id}` }),
      providesTags: (result, error, id) => [{ type: 'PosDraft', id }],
    }),
    createPosDraft: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/pos/draft_sales',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PosDraft'],
    }),
    updatePosDraft: builder.mutation<any, { id: number | string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        // PUT draft_sales/{id} has no `pos/` prefix on the backend
        url: `/draft_sales/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'PosDraft', id }, 'PosDraft'],
    }),
    deletePosDraft: builder.mutation<void, number | string>({
      query: (id) => ({
        url: `/pos/draft_sales/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'PosDraft', id }, 'PosDraft'],
    }),
    submitPosDraft: builder.mutation<any, { id: number | string; data?: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/pos/draft_sales/${id}/submit`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PosDraft', 'POS', 'Sales'],
    }),
    getDraftConversionData: builder.query<any, number | string>({
      query: (id) => ({ url: `/pos/data_draft_convert_sale/${id}` }),
      providesTags: (result, error, id) => [{ type: 'PosDraft', id }],
    }),

    // --- POS Products ---
    getPosProducts: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/pos/products',
        params: params || {},
      }),
      providesTags: ['PosProduct'],
    }),
    searchPosProducts: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/pos/products/search',
        params: params || {},
      }),
      providesTags: ['PosProduct'],
    }),

    // --- POS Customers ---
    getPosCustomersWithoutPaginate: builder.query<any, void>({
      // Backend route: Get_Customers_Without_Paginate
      query: () => ({ url: '/Get_Customers_Without_Paginate' }),
      providesTags: ['PosCustomer'],
    }),
    checkPosCreditEligibility: builder.mutation<any, { customer_id: number | string; [key: string]: any }>({
      query: ({ customer_id, ...params }) => ({
        url: '/customers/check-credit-eligibility',
        method: 'POST',
        data: { customer_id, ...params },
      }),
    }),
    getPreSaleValidation: builder.query<any, { customerId: number | string; params?: Record<string, any> }>({
      query: ({ customerId, params }) => ({
        url: `/customers/${customerId}/pre-sale-validation`,
        params: params || {},
      }),
      providesTags: ['Customer'],
    }),

    // --- POS Units (backend: GET get_product_units/{id}) ---
    getPosUnitsByProduct: builder.query<any, number | string>({
      query: (productId) => ({ url: `/get_product_units/${productId}` }),
      providesTags: ['PosProduct'],
    }),

    // --- POS Settings ---
    getPosSettings: builder.query<any, void>({
      query: () => ({ url: '/pos/settings' }),
      providesTags: ['Settings'],
    }),
    updatePosSettings: builder.mutation<any, { id: number | string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        // Backend route: PUT pos/settings/{id}
        url: `/pos/settings/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Settings'],
    }),
  }),
  overrideExisting: false,
})

export const {
  // POS Data
  useGetPosDataQuery,
  useLazyGetPosDataQuery,
  useGetTodaySalesQuery,
  useLazyGetTodaySalesQuery,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
  useCreatePosSaleMutation,
  useSyncPosSalesMutation,
  useGetProductStockQuery,
  useLazyGetProductStockQuery,
  useGetProductsByWarehouseQuery,
  useLazyGetProductsByWarehouseQuery,

  // POS Drafts
  useGetPosDraftsQuery,
  useLazyGetPosDraftsQuery,
  useGetPosDraftByIdQuery,
  useLazyGetPosDraftByIdQuery,
  useCreatePosDraftMutation,
  useUpdatePosDraftMutation,
  useDeletePosDraftMutation,
  useSubmitPosDraftMutation,
  useGetDraftConversionDataQuery,
  useLazyGetDraftConversionDataQuery,

  // POS Products
  useGetPosProductsQuery,
  useLazyGetPosProductsQuery,
  useSearchPosProductsQuery,
  useLazySearchPosProductsQuery,

  // POS Customers
  useGetPosCustomersWithoutPaginateQuery,
  useLazyGetPosCustomersWithoutPaginateQuery,
  useCheckPosCreditEligibilityMutation: useCheckCreditEligibilityMutation,
  useGetPreSaleValidationQuery,
  useLazyGetPreSaleValidationQuery,

  // POS Units
  useGetPosUnitsByProductQuery,
  useLazyGetPosUnitsByProductQuery,

  // POS Settings
  useGetPosSettingsQuery,
  useLazyGetPosSettingsQuery,
  useUpdatePosSettingsMutation,
} = posApi
